import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { Button } from '../../../components-v2/Button';
import { styleSheet } from '../styles';
import { CalendarViewMode } from '../types';
import { ACTION_ITEM_COLOR, EVENT_COLOR, MEETING_LEGEND_COLOR } from '../utils/normalize';
import { CalendarUserSelect } from './CalendarUserSelect';
import { CalendarViewToolbar } from './CalendarViewToolbar';

interface IProps {
	viewMode: CalendarViewMode;
	onViewModeChange: (mode: CalendarViewMode) => void;
	title: string;
	onPrevious: () => void;
	onNext: () => void;
	onToday: () => void;
	currentUser: Api.IUser | null;
	selectedUser: Api.IUser | null;
	onUserChange: (user: Api.IUser | null) => void;
	/** Admins with scheduler reporting can view other users; others are locked to themselves. */
	canBrowseAll: boolean;
	showMeetings: boolean;
	onToggleMeetings: () => void;
	showEvents: boolean;
	onToggleEvents: () => void;
	/** Whether the events legend/toggle should be shown at all (Surveys feature on). */
	eventsAvailable: boolean;
	showActionItems: boolean;
	onToggleActionItems: () => void;
}

/**
 * A clickable legend chip that doubles as a show/hide toggle for an item type. Uses the
 * standard outline/sm Button so it lines up with the rest of the toolbar controls; the
 * "off" state dims it.
 */
const LegendToggle: React.FC<{ label: string; color: string; on: boolean; onToggle: () => void }> = ({
	label,
	color,
	on,
	onToggle,
}) => (
	<Button
		variant='outline'
		size='sm'
		styles={[!on && styleSheet.legendChipOff]}
		onClick={onToggle}
		aria-pressed={on}
		title={on ? `Hide ${label.toLowerCase()}` : `Show ${label.toLowerCase()}`}
	>
		<span className={css(styleSheet.legendDot)} style={{ backgroundColor: color }} />
		{label}
	</Button>
);

/** Header controls for the standalone calendar: shared nav/switcher plus host filter and type legend. */
export const CalendarToolbar: React.FC<IProps> = ({
	viewMode,
	onViewModeChange,
	title,
	onPrevious,
	onNext,
	onToday,
	currentUser,
	selectedUser,
	onUserChange,
	canBrowseAll,
	showMeetings,
	onToggleMeetings,
	showEvents,
	onToggleEvents,
	eventsAvailable,
	showActionItems,
	onToggleActionItems,
}) => {
	return (
		<CalendarViewToolbar
			viewMode={viewMode}
			onViewModeChange={onViewModeChange}
			title={title}
			onPrevious={onPrevious}
			onNext={onNext}
			onToday={onToday}
		>
			<div className={css(styleSheet.legend)}>
				<LegendToggle label='Meetings' color={MEETING_LEGEND_COLOR} on={showMeetings} onToggle={onToggleMeetings} />
				{eventsAvailable ? (
					<LegendToggle label='Events' color={EVENT_COLOR} on={showEvents} onToggle={onToggleEvents} />
				) : null}
				<LegendToggle
					label='Action Items'
					color={ACTION_ITEM_COLOR}
					on={showActionItems}
					onToggle={onToggleActionItems}
				/>
			</div>
			<CalendarUserSelect
				currentUser={currentUser}
				selectedUser={selectedUser}
				onChange={onUserChange}
				canBrowseAll={canBrowseAll}
			/>
		</CalendarViewToolbar>
	);
};
